import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, asyncScheduler, map, mergeMap, scheduled } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { securedResources } from '../../msal.config';

/**
 * Interceptor to handle secured ressources
 * when user is logged in and request url is matching secured endpoints
 * accessToken is add to request header
 * @class TokenInterceptor
 * @typedef {TokenInterceptor}
 * @implements {HttpInterceptor}
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    #authService = inject( AuthService );

    /**
     * interceptor to check logged in state and url match
     * triggers auth request preparation and handles it when true
     * otherwise request is passed through
     */
    intercept( request: HttpRequest<unknown>, next: HttpHandler ): Observable<HttpEvent<unknown>> {
        const loggedIn = this.#authService.isLoggedIn();
        const urlMatch = securedResources.find( ( resource ) => request.url.includes( resource ) );

        if ( loggedIn && urlMatch ) {
            return this.prepareAuthRequest( request ).pipe(
                mergeMap( ( authRequest: HttpRequest<unknown> ) => next.handle( authRequest ) )
            );
        }

        return next.handle( request );
    }

    /**
     * retrives accessToken from authService
     * add auth header to request
     * @param {HttpRequest<unknown>} request - incoming secured api call
     * @returns {Observable<HttpRequest<unknown>>} - outgoing auth api call
     */
    prepareAuthRequest( request: HttpRequest<unknown> ): Observable<HttpRequest<unknown>> {
        return scheduled( this.#authService.retrieveAccessToken(), asyncScheduler ).pipe(
            map( ( token ) => {
                const authRequest = request.clone( {
                    setHeaders: { Authorization: `Bearer ${token}` }
                } );
                return authRequest;
            } )
        );
    }
}
